import { useCallback, useEffect, useRef, useState } from 'react'

import { useResizeObserver } from './useResizeObserver'

export interface Rect {
  bottom: number
  height: number
  left: number
  right: number
  top: number
  width: number
  x: number
  y: number
}

export const emptyRect = {
  bottom: 0,
  height: 0,
  left: 0,
  right: 0,
  top: 0,
  width: 0,
  x: 0,
  y: 0,
}

export function useElementSizeCallback(
  element: Element | null | undefined,
  cb: (rect: Rect) => void,
) {
  const callback = useCallback(() => {
    if (element) {
      cb(element.getBoundingClientRect().toJSON())
    }
  }, [element, cb])

  useEffect(callback, [callback])
  useResizeObserver(element, callback)
}

/**
 * @deprecated the return value is mostly useless because it's often incorrect since this hook doesn't trigger rerendering. Keeping it around to support legacy code that will be deleted soon
 */
export function useElementSizeRef(
  element: Element | null | undefined,
  cb?: (rect: Rect) => void,
) {
  const ref = useRef<Rect>(emptyRect)
  useElementSizeCallback(
    element,
    useCallback(
      rect => {
        ref.current = rect
        if (cb) {
          cb(rect)
        }
      },
      [ref],
    ),
  )
  return ref
}

export function useElementSize(element: Element | null | undefined) {
  const [rect, setRect] = useState<Rect>(emptyRect)
  useElementSizeCallback(element, setRect)
  return rect
}

export function useDocumentSize() {
  return useElementSize(document.scrollingElement)
}

export function useDocumentSizeCallback(cb: (rect: Rect) => void) {
  useElementSizeCallback(document.scrollingElement, cb)
}
